/**
 * <ObjectiveHoursChip> — un objectif pédagogique, en une chip (molecule, L0.1).
 *
 * Les icônes de matières empilées, puis la matière et le volume : « Mathématiques · 2 h ».
 * UNE chip par OBJECTIF, jamais par matière — c'est ce qui garde la somme des heures affichées
 * égale aux heures souscrites (`ParentSubscriptionPortalScreen.js:771-781`).
 *
 * Le libellé compte les matières comme la pile, avec le texte d'une séance (`subjectSummary`) :
 * jusqu'à trois, toutes nommées en entier ; au-delà, deux et « +N » (« Maths, Physique-Chimie +3 »).
 * Il porte aussi le sens quand une icône ne charge pas — la chip ne repose donc jamais sur la
 * seule image.
 *
 * Composition : `<SubjectBadges showLabel={false}>` pour la pile d'icônes, `<Badge tinted>` pour
 * le texte. Là où le legacy éclaircissait l'hexadécimal de la matière de 90 %, ce sont les jetons
 * qui portent la teinte.
 */
import React from "react"

import { Badge } from "../../atoms/Badge"
import { Row } from "../../atoms/containers/Row"
import { SubjectBadges } from "../SubjectBadges"
import { subjectSummary } from "../../../ports/primary/ui/formatters/liveSessionFormatter"
import { type UISubject } from "../../../ports/primary/ui/types"

export type ObjectiveHoursChipProps = {
    subjects: UISubject[]
    /** Volume hebdomadaire alloué à l'objectif — absent ou nul → la chip ne porte que ses matières. */
    hours?: number | null
    testID?: string
}

export function ObjectiveHoursChip(props: ObjectiveHoursChipProps) {
    const { subjects, hours, testID } = props

    const showHours = hours != null && hours > 0
    // Ni matière ni volume : le legacy saute la chip plutôt que de rendre une pastille vide.
    if (subjects.length === 0 && !showHours) return null

    const text = [subjectSummary(subjects), showHours ? `${hours} h` : null].filter(Boolean).join(" · ")

    return (
        <Row gap="1" align="center" testID={testID}>
            <SubjectBadges subjects={subjects} showLabel={false} />
            {/* Le texte est déjà borné (deux noms au plus, puis « +N ») : la chip ne le coupe pas. */}
            {text !== "" && <Badge text={text} variant="neutral" tinted truncate={false} />}
        </Row>
    )
}
